
import React, { useState } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { DialogClose } from '@/components/ui/dialog';
import CancelAppointmentDialog, {
  type CancelAppointmentScope,
} from '@/components/appointments/CancelAppointmentDialog';
import { Appointment, Client } from '@/components/appointments/AppointmentCard';
import { Calendar, Clock, Video, User, Mail, Repeat, Trash2, Edit2, Copy } from 'lucide-react';
import { formatInTimezone, formatTimeRangeInTimezone } from '@/utils/timezone-utils';
import {
  shouldHideAppointmentJoinRescheduleCancel,
  canJoinAppointment,
  getVideoMeetingPlatformLabel,
} from '@/utils/appointments';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';

interface AppointmentDetailsProps {
  appointment: Appointment;
  client: Client | undefined;
  onEdit: () => void;
  onDelete: (scope?: CancelAppointmentScope) => void | Promise<void>;
  /** When set (e.g. full-page / embedded view), Close uses this instead of DialogClose. */
  onClose?: () => void;
}

const AppointmentDetails: React.FC<AppointmentDetailsProps> = ({
  appointment,
  client,
  onEdit,
  onDelete,
  onClose,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const appointmentData = appointment as any;
  const title = appointmentData.title || 'Appointment';
  const description = appointmentData.description || '';
  const statusRaw = appointmentData.status;
  // Check for meeting link in multiple possible property names
  const meetingLink = appointmentData.meetingLink || appointmentData.meeting_link || null;

  // API returns startTime and endTime as ISO datetime strings
  const startDate = appointmentData.startTime ? new Date(appointmentData.startTime) : appointment.date;
  const endDate = appointmentData.endTime ? new Date(appointmentData.endTime) : new Date(appointment.date.getTime() + appointment.duration * 60000);
  const hideLifecycleActions = shouldHideAppointmentJoinRescheduleCancel(statusRaw, endDate, {
    startAt: startDate,
    durationMinutes: appointment.duration,
  });
  const canJoinNow = Boolean(
    meetingLink &&
      canJoinAppointment(statusRaw, startDate, endDate, {
        startAt: startDate,
        durationMinutes: appointment.duration,
      }),
  );
  const meetingPlatformLabel = meetingLink ? getVideoMeetingPlatformLabel(meetingLink) : '';

  const handleCopyMeetingLink = async () => {
    if (!meetingLink) return;
    try {
      await navigator.clipboard.writeText(meetingLink);
      showSuccessToast('Link copied', 'Meeting link copied to clipboard.');
    } catch {
      showErrorToast(new Error('Copy failed'), 'Could not copy meeting link');
    }
  };

  // Format times in user's local timezone (not the appointment's stored timezone)
  // The Date objects are already in local timezone, so we can use date-fns format directly
  const startTimeStr = format(startDate, 'h:mm a');
  const endTimeStr = format(endDate, 'h:mm a');
  const timeRangeStr = `${startTimeStr} - ${endTimeStr}`;
  const dateStr = format(startDate, 'EEEE, MMMM d, yyyy');

  // Get recurrence text
  const getRecurrenceText = () => {
    switch (appointment.recurrencePattern) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'biweekly': return 'Every two weeks';
      case 'monthly': return 'Monthly';
      default: return '';
    }
  };

  const handleDeleteConfirm = async (scope: CancelAppointmentScope) => {
    setIsDeleting(true);
    try {
      await onDelete(scope);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsDeleting(false);
    }
  };

  const appointmentRecord = appointment as unknown as {
    recurringDetails?: { isModified?: boolean; isParent?: boolean };
  };

  return (
    <div className="py-1">
      {/* Title - Large and prominent */}
      <div className="mb-5">
        <h2 className="text-xl sm:text-2xl font-semibold text-foreground leading-tight">{title}</h2>
      </div>

      {/* Date and Time Section */}
      <div className="space-y-3.5 mb-5">
        {/* Date */}
        <div className="flex items-start gap-3">
          <Calendar className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-foreground font-medium">{dateStr}</div>
          </div>
        </div>

        {/* Time */}
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-foreground font-medium">
              {timeRangeStr}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {appointment.duration} minutes
            </div>
          </div>
        </div>

        {/* Meeting Link / Video Call */}
        {meetingLink && (
          <div className="flex items-start gap-3">
            <Video className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="text-xs text-muted-foreground font-medium">{meetingPlatformLabel}</div>
              {canJoinNow ? (
                <a
                  href={meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium inline-flex items-center gap-1"
                >
                  Join {meetingPlatformLabel}
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
              ) : (
                <a
                  href={meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium break-all"
                >
                  {meetingLink}
                </a>
              )}
              <button
                type="button"
                onClick={() => void handleCopyMeetingLink()}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <Copy className="h-3 w-3" />
                Copy link
              </button>
            </div>
          </div>
        )}

        {/* Client/Attendee */}
        {client && (
          <>
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-foreground font-medium">{client.name}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <a
                  href={`mailto:${client.email}`}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {client.email}
                </a>
              </div>
            </div>
          </>
        )}

        {/* Recurring */}
        {appointment.isRecurring && (
          <div className="flex items-start gap-3">
            <Repeat className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-foreground font-medium">{getRecurrenceText()}</div>
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      {description && (
        <div className="mb-5 pt-4 border-t border-border/50">
          <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{description}</div>
        </div>
      )}

      {/* Actions - Bottom aligned like Google Calendar */}
      <div
        className={`flex flex-col-reverse sm:flex-row sm:items-center gap-3 pt-5 border-t border-border/50 mt-6 ${
          hideLifecycleActions ? 'sm:justify-end' : 'sm:justify-between'
        }`}
      >
        {!hideLifecycleActions && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10 w-full sm:w-auto"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
          {onClose ? (
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="w-full sm:w-auto">
              Back
            </Button>
          ) : (
            <DialogClose asChild>
              <Button variant="outline" size="sm" className="w-full sm:w-auto">Close</Button>
            </DialogClose>
          )}
          {!hideLifecycleActions && (
            <Button onClick={onEdit} size="sm" className="bg-cs-primary hover:bg-cs-primary/90 w-full sm:w-auto">
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {!hideLifecycleActions && (
        <CancelAppointmentDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          appointment={appointment}
          clientName={client?.name}
          isRecurring={Boolean(appointment.isRecurring)}
          isModified={appointmentRecord.recurringDetails?.isModified === true}
          isParent={appointmentRecord.recurringDetails?.isParent === true}
          isCancelling={isDeleting}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default AppointmentDetails;
